import { httpClient } from './httpClient';

export interface AuditEventResponse {
  id: string;
  eventType: string;
  caseId: string | null;
  caseName: string | null;
  userId: string;
  description: string;
  details: Record<string, any> | null;
  previousState: Record<string, any> | null;
  newState: Record<string, any> | null;
  status: 'success' | 'warning' | 'error';
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface AuditPageResponse {
  content: AuditEventResponse[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

export interface AuditQueryParams {
  page?: number;
  size?: number;
  caseId?: string;
  dateRange?: string;
  search?: string;
  status?: string;
}

// Intelligence service audit trail has different shape
interface ChronologyAuditEntry {
  id: string;
  action: string;
  userId: string;
  eventId: string | null;
  actionDescription: string;
  details: Record<string, any> | null;
  previousState: Record<string, any> | null;
  newState: Record<string, any> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface ChronologyAuditPageResponse {
  content: ChronologyAuditEntry[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

function mapChronologyAuditToEvent(entry: ChronologyAuditEntry, caseName?: string): AuditEventResponse {
  return {
    id: entry.id,
    eventType: entry.action,
    caseId: null,
    caseName: caseName || null,
    userId: entry.userId,
    description: entry.actionDescription,
    details: entry.details,
    previousState: entry.previousState,
    newState: entry.newState,
    status: 'success',
    ipAddress: entry.ipAddress,
    userAgent: entry.userAgent,
    createdAt: entry.createdAt,
  };
}

export const auditApi = {
  /**
   * Fetch case-level audit events from case-service.
   */
  getCaseAuditEvents: async (params: AuditQueryParams = {}): Promise<AuditPageResponse> => {
    return httpClient.get<AuditPageResponse>('/cases/audit', params);
  },

  /**
   * Fetch chronology audit trail from intelligence-service for a specific case.
   */
  getChronologyAuditEvents: async (caseId: string, params: AuditQueryParams = {}): Promise<AuditPageResponse> => {
    try {
      const response = await httpClient.get<ChronologyAuditPageResponse>(
        `/chronologies/audit/case/${caseId}`,
        { page: params.page || 0, size: params.size || 20 }
      );

      return {
        content: response.content.map(entry => mapChronologyAuditToEvent(entry)),
        totalElements: response.totalElements,
        totalPages: response.totalPages,
        number: response.number,
        size: response.size,
        first: response.first,
        last: response.last,
      };
    } catch {
      // No chronology for this case yet — return empty
      return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 20, first: true, last: true };
    }
  },

  /**
   * Fetch all audit events (case + chronology), merged and sorted by timestamp.
   */
  getAllAuditEvents: async (params: AuditQueryParams = {}): Promise<{
    events: AuditEventResponse[];
    totalElements: number;
    hasMore: boolean;
  }> => {
    const caseEvents = await auditApi.getCaseAuditEvents(params);

    let chronologyEvents: AuditEventResponse[] = [];
    if (params.caseId) {
      const chronoResponse = await auditApi.getChronologyAuditEvents(params.caseId, params);
      chronologyEvents = chronoResponse.content;
    }

    // Merge and sort by timestamp descending
    const allEvents = [...caseEvents.content, ...chronologyEvents]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return {
      events: allEvents,
      totalElements: caseEvents.totalElements + chronologyEvents.length,
      hasMore: !caseEvents.last,
    };
  },
};
